import { useCallback, useState } from "react";
import styled from "styled-components";
import {
  Button,
  Card,
  Field,
  Input,
  Modal,
  Select,
  Icon,
  SkeletonLoader,
  Stack,
  Tag,
  Title,
  useActions,
  useResponsive,
  useToast
} from "@introist/react-foundation/v2";

import { ListView } from "../../../../foundation";
import { useFeatureFlag } from "../../../../hooks";
import { List, ListRow, NoResults } from "../../../../components/organisms";
import { TopBarRoute } from "../../../../components/templates";
import { useAppMenuContext } from "../../../../routes/App/shell";
import {
  CreatedPlatformToken,
  PLATFORM_OPENAPI_URL,
  PLATFORM_SKILL_URL,
  PlatformToken,
  tokenState,
  useCreatePlatformToken,
  usePlatformTokens,
  useRevokePlatformToken
} from "../../../../services/api/PlatformTokensApi";

const EXPIRY_OPTIONS = [
  { key: "30", title: "30 days" },
  { key: "90", title: "90 days" },
  { key: "180", title: "180 days" },
  { key: "365", title: "1 year" }
];

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : "–");

const SecretBox = styled.code`
  display: block;
  padding: var(--spacing-medium);
  border-radius: var(--rounding-medium);
  background: var(--palette-surface-dimmed);
  word-break: break-all;
  user-select: all;
`;

const stateTag = (token: PlatformToken) => {
  const state = tokenState(token);
  if (state === "active") return <Tag colorVariant="success">Active</Tag>;
  if (state === "expired") return <Tag colorVariant="warning">Expired</Tag>;
  return <Tag colorVariant="surface">Revoked</Tag>;
};

const CreateTokenModal = ({
  open,
  onClose,
  onCreated
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (token: CreatedPlatformToken) => void;
}) => {
  const toast = useToast();
  const createToken = useCreatePlatformToken();
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState("90");

  const onCreate = useCallback(async () => {
    await createToken({ name: name.trim(), expiresInDays: Number(expiry) })
      .then(created => {
        setName("");
        setExpiry("90");
        onCreated(created);
      })
      .catch((e: Error) => toast.error(e.message || "Failed to create token"));
  }, [createToken, name, expiry, onCreated, toast]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create API token"
      style={{ width: "480px" }}
      primaryButton={{
        title: "Create token",
        onClickWithLoading: onCreate,
        disabled: name.trim().length === 0
      }}
    >
      <Stack vertical gap="large">
        <Field title="Name">
          <Input
            value={name}
            onChange={setName}
            placeholder="e.g. Claude Code on my laptop"
            maxLength={100}
            autoFocus
          />
        </Field>
        <Field title="Expires in">
          <Select
            style={{ width: "100%" }}
            options={EXPIRY_OPTIONS}
            value={expiry}
            onSelect={opt => setExpiry(opt.key)}
          />
        </Field>
        <Title>
          The token acts as you, with your rights, in every workspace you belong to. Treat it like a
          password.
        </Title>
      </Stack>
    </Modal>
  );
};

const ShowTokenModal = ({
  token,
  onClose
}: {
  token: CreatedPlatformToken | null;
  onClose: () => void;
}) => {
  const toast = useToast();

  const copy = useCallback(async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token.token);
      toast.success("Token copied");
    } catch {
      toast.error("Copy failed, select the token and copy it manually");
    }
  }, [token, toast]);

  return (
    <Modal
      open={!!token}
      onClose={onClose}
      title="Copy your new token"
      style={{ width: "520px" }}
      primaryButton={{ title: "Copy token", onClick: copy }}
      secondaryButton={{ title: "Done", onClick: onClose }}
    >
      <Stack vertical gap="large">
        <Title>This is the only time the token is shown. Store it somewhere safe now.</Title>
        <SecretBox>{token?.token}</SecretBox>
        <Title>
          For agents, set it as the <code>INTROIST_API_TOKEN</code> environment variable rather than
          pasting it into a chat.
        </Title>
      </Stack>
    </Modal>
  );
};

// User-level, not workspace settings: a token acts as you in every workspace you belong to. It's
// opened from the workspace selector.
export const ApiTokensRoute = () => {
  const { isEnabled } = useFeatureFlag("platform");
  const { toggleMenuCollapsed } = useAppMenuContext();
  const { isMobileLayout } = useResponsive();

  return (
    <TopBarRoute
      narrow
      navigationAction={isMobileLayout ? "menu" : undefined}
      onNavigationAction={isMobileLayout ? toggleMenuCollapsed : undefined}
      paths={[{ key: "apiTokens", title: "API tokens" }]}
    >
      {isEnabled ? <ApiTokens /> : <NoResults title="API tokens are not available yet" />}
    </TopBarRoute>
  );
};

const ApiTokens = () => {
  const toast = useToast();
  const { onConfirmAction } = useActions();

  const { data: tokens, isLoading, isError } = usePlatformTokens();
  const revokeOp = useRevokePlatformToken();

  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreatedPlatformToken | null>(null);

  const revoke = (token: PlatformToken) =>
    onConfirmAction(
      async () => {
        await revokeOp
          .mutateAsync(token.id)
          .then(() => toast.success("Token revoked"))
          .catch((e: Error) => toast.error(e.message || "Failed to revoke token"));
      },
      {
        title: "Revoke token",
        description: `"${token.name}" stops working immediately. This can't be undone.`
      }
    )();

  return (
    <Stack vertical gap="xLarge">
      <Card spacing="xLarge">
        <Stack vertical gap="xxLarge">
          <ListView.Header
            title="API tokens"
            iconName="key"
            create={
              <Button startAdornment={<Icon name="plus" />} onClick={() => setCreateOpen(true)}>
                Create token
              </Button>
            }
          />
          <Title>
            Personal access tokens let tools and AI agents use the{" "}
            <a href={PLATFORM_OPENAPI_URL} target="_blank" rel="noreferrer">
              Introist Platform API
            </a>{" "}
            as you, in every workspace you belong to. Changes made through the API are planned first and only applied after
            confirmation, and every call is recorded in the audit log. For AI agents, download the{" "}
            <a href={PLATFORM_SKILL_URL}>Introist skill</a> and unzip it into your skills folder
            (e.g. <code>~/.claude/skills/</code>).
          </Title>

          {isLoading && (
            <Stack vertical gap="small">
              <SkeletonLoader height={48} />
              <SkeletonLoader height={48} />
              <SkeletonLoader fadeOut height={48} />
            </Stack>
          )}
          {isError && <NoResults title="Failed to load tokens" />}
          {tokens && tokens.length === 0 && (
            <NoResults title="No tokens yet" message="Create a token to connect a tool or agent." />
          )}
          {tokens && tokens.length > 0 && (
            <List>
              {tokens.map(token => (
                <ListRow key={token.id} gridTemplateColumns="1fr 140px 110px 110px 90px 100px">
                  <Stack vertical gap="xSmall">
                    <Title variant="bold">{token.name}</Title>
                    <Title small>{token.tokenPrefix}…</Title>
                  </Stack>
                  <Title small>Last used {formatDate(token.lastUsedAt)}</Title>
                  <Title small>Created {formatDate(token.createdAt)}</Title>
                  <Title small>Expires {formatDate(token.expiresAt)}</Title>
                  {stateTag(token)}
                  {tokenState(token) === "active" ? (
                    <Button variant="outlined" size="small" onClick={() => revoke(token)}>
                      Revoke
                    </Button>
                  ) : (
                    <span />
                  )}
                </ListRow>
              ))}
            </List>
          )}
        </Stack>
      </Card>

      <CreateTokenModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={token => {
          setCreateOpen(false);
          setCreated(token);
        }}
      />
      <ShowTokenModal token={created} onClose={() => setCreated(null)} />
    </Stack>
  );
};
